/**
 * App - top-level shell.
 *
 * Owns the active channel (CHILL/NSFW), the current view (For You / Favorites /
 * Chats / a single Chat), the per-channel Favorites store and the chat store.
 * Favorites are scoped to the channel kind so NSFW saves never appear in CHILL.
 */
import { Fragment } from 'preact';
import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { useVideoFeed } from './hooks/useVideoFeed.js';
import { useFavorites } from './hooks/useFavorites.js';
import { useChat } from './hooks/useChat.js';
import { Feed } from './components/Feed.jsx';
import { FavoritesView } from './components/FavoritesView.jsx';
import { TabBar } from './components/TabBar.jsx';
import { ChatList } from './components/ChatList.jsx';
import { ChatView } from './components/ChatView.jsx';
import { ChatGate } from './components/ChatGate.jsx';
import {
  resolveContentId,
  selectContentId,
  isChillChannel,
  channelKind,
  getNsfwContentId,
  CHILL_CONTENT_ID,
} from './lib/content.js';
import { consumeRewardedCheck } from './lib/chat.js';
import { maybeShowRewarded } from './bridge/puerInterface.js';

export function App() {
  const [view, setView] = useState('foryou'); // foryou | favorites | chats | chat
  const [contentId, setContentId] = useState(resolveContentId);
  const [activeChatId, setActiveChatId] = useState(null);
  const [chatGateVideo, setChatGateVideo] = useState(null);
  const prevView = useRef('foryou');

  const kind = channelKind(contentId);
  const isNsfw = kind === 'nsfw';

  const favorites = useFavorites(kind);
  const chat = useChat();

  const { videos, setVideos, feed, loading, usingMock } = useVideoFeed(contentId);

  const toggleChannel = useCallback(() => {
    const next = isChillChannel(contentId) ? getNsfwContentId() : CHILL_CONTENT_ID;
    selectContentId(next);
    setContentId(next);
  }, [contentId]);

  /* ---- chat flow ------------------------------------------------- */
  // Tapping the author-chat icon: run the (rate-limited) rewarded check, then
  // show the 3s "Waiting for the author" gate before opening the chat.
  const requestChat = useCallback((video) => {
    if (consumeRewardedCheck()) maybeShowRewarded();
    setChatGateVideo(video);
  }, []);

  const onGateReady = useCallback(() => {
    setChatGateVideo((video) => {
      if (video) {
        const id = chat.open({
          authorId: String(video.author || video.id),
          authorName: video.author || video.title || '',
          channel: kind,
        });
        prevView.current = 'foryou';
        chat.read(id);
        setActiveChatId(id);
        setView('chat');
      }
      return null;
    });
  }, [chat, kind]);

  const openChat = useCallback(
    (id) => {
      prevView.current = view === 'chat' ? prevView.current : view;
      chat.read(id);
      setActiveChatId(id);
      setView('chat');
    },
    [view, chat],
  );

  const backFromChat = useCallback(() => {
    setActiveChatId(null);
    setView(prevView.current || 'foryou');
  }, []);

  const demoBadge = usingMock ? <span class="topbar__demo">DEMO</span> : null;
  const activeChat = chat.chats.find((c) => c.id === activeChatId) || null;

  // If a reply lands while its chat is open, clear the unread badge live.
  useEffect(() => {
    if (view === 'chat' && activeChat?.unread) chat.read(activeChat.id);
  }, [view, activeChat, chat]);

  return (
    <Fragment>
      {view === 'foryou' && (
        <Feed
          key={contentId}
          videos={videos}
          setVideos={setVideos}
          feed={feed}
          content={contentId}
          loading={loading}
          enableAds
          enableTracking
          showChannelToggle
          isChill={isChillChannel(contentId)}
          onToggleChannel={toggleChannel}
          favorites={favorites}
          showChat={isNsfw}
          onRequestChat={requestChat}
          topBar={demoBadge}
        />
      )}

      {view === 'favorites' && (
        <FavoritesView
          favorites={favorites}
          showChat={isNsfw}
          onRequestChat={requestChat}
          onReachEnd={() => setView('foryou')}
        />
      )}

      {view === 'chats' && (
        <ChatList chats={chat.chats} onOpen={openChat} />
      )}

      {view === 'chat' && (
        <ChatView
          chat={activeChat}
          onBack={backFromChat}
          onSend={chat.send}
          onSendMedia={chat.sendMedia}
          onEdit={chat.edit}
          onEndCall={chat.endCall}
        />
      )}

      {chatGateVideo && <ChatGate onReady={onGateReady} />}

      {(
        <TabBar
          active={view}
          favoritesCount={favorites.favorites.length}
          chatsCount={chat.chats.length}
          chatsUnread={chat.unread}
          onChange={setView}
        />
      )}
    </Fragment>
  );
}
